import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Pagination,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  TrendingUp,
  TrendingDown,
  Add,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { format } from 'date-fns';

interface Transaction {
  id: string;
  amount: number;
  source: string;
  reason: string;
  previousCredits: number;
  newCredits: number;
  availableBalance: number;
  metadata: any;
  timestamp: string;
  createdAt: string;
  updatedAt: string;
}

interface UserInfo {
  id: string;
  firebaseUserId: string;
  email?: string;
  displayName?: string;
  credits: number;
}

const UserTransactions: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 1,
  });
  const [addCreditsDialogOpen, setAddCreditsDialogOpen] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsReason, setCreditsReason] = useState('');
  const [addingCredits, setAddingCredits] = useState(false);

  useEffect(() => {
    if (userId) {
      loadTransactions();
    }
  }, [userId, pagination.page]);

  const loadTransactions = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getUserTransactions(
        userId,
        pagination.page,
        pagination.limit
      );

      if (response.success && response.data) {
        setTransactions(response.data.transactions || []);
        setUser(response.data.user || null);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.pagination?.totalCount || 0,
          totalPages: response.data.pagination?.totalPages || 1,
        }));
      } else {
        setError('Failed to load transactions');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const getSourceLabel = (source: string) => {
    const labels: { [key: string]: string } = {
      photo_swap: 'Photo Swap',
      group_photo_swap: 'Group Photo Swap',
      video_swap: 'Video Swap',
      enhance_image: 'Enhance Image',
      restore_image: 'Restore Image',
      watermark_removal: 'Watermark Removal',
      premium_template: 'Premium Template',
      purchase: 'Purchase',
      rewarded_ad: 'Rewarded Ad',
      admin: 'Admin Adjustment',
      refund: 'Refund',
      bonus: 'Bonus',
    };
    return labels[source] || source;
  };

  const getSourceColor = (source: string) => {
    const colors: { [key: string]: 'primary' | 'success' | 'error' | 'warning' | 'info' | 'default' } = {
      photo_swap: 'primary',
      group_photo_swap: 'primary',
      video_swap: 'primary',
      enhance_image: 'primary',
      restore_image: 'primary',
      watermark_removal: 'warning',
      premium_template: 'info',
      purchase: 'success',
      rewarded_ad: 'success',
      admin: 'default',
      refund: 'success',
      bonus: 'success',
    };
    return colors[source] || 'default';
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  if (loading && transactions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">Credit Transaction History</Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<Add />}
            onClick={() => setAddCreditsDialogOpen(true)}
            variant="contained"
            color="success"
          >
            Add Credits
          </Button>
          <Button
            startIcon={<Refresh />}
            onClick={loadTransactions}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {user && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  User
                </Typography>
                <Typography variant="h6">
                  {user.displayName || user.email || 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{user.email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Current Credits
                </Typography>
                <Typography variant="h6" color="primary">
                  {user.credits || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Transactions
                </Typography>
                <Typography variant="h6">{pagination.totalCount}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date & Time</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Previous Balance</TableCell>
              <TableCell>New Balance</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No transactions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell>
                    {transaction.timestamp
                      ? format(new Date(transaction.timestamp), 'MMM dd, yyyy HH:mm:ss')
                      : format(new Date(transaction.createdAt), 'MMM dd, yyyy HH:mm:ss')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getSourceLabel(transaction.source)}
                      color={getSourceColor(transaction.source)}
                      size="small"
                    />
                    {transaction.reason && transaction.reason !== transaction.source && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        {transaction.reason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {transaction.amount > 0 ? (
                        <TrendingUp color="success" fontSize="small" />
                      ) : (
                        <TrendingDown color="error" fontSize="small" />
                      )}
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                      >
                        {transaction.amount > 0 ? '+' : ''}
                        {transaction.amount}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{transaction.previousCredits}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {transaction.newCredits}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {JSON.stringify(transaction.metadata)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Add Credits Dialog */}
      <Dialog
        open={addCreditsDialogOpen}
        onClose={() => {
          setAddCreditsDialogOpen(false);
          setCreditsAmount('');
          setCreditsReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Credits to User</DialogTitle>
        <DialogContent>
          {user && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                User: {user.displayName || user.email || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Credits: {user.credits || 0}
              </Typography>
              <TextField
                label="Amount"
                type="number"
                fullWidth
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(e.target.value)}
                margin="normal"
                inputProps={{ min: 1, step: 1 }}
                helperText="Enter the number of credits to add"
              />
              <TextField
                label="Reason (Optional)"
                fullWidth
                value={creditsReason}
                onChange={(e) => setCreditsReason(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                placeholder="e.g., Bonus credits, Refund, Promotional credits"
                helperText="Optional reason for adding credits"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddCreditsDialogOpen(false);
              setCreditsAmount('');
              setCreditsReason('');
            }}
            disabled={addingCredits}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!userId || !creditsAmount) return;
              
              const amount = parseInt(creditsAmount);
              if (isNaN(amount) || amount <= 0) {
                alert('Please enter a valid positive number');
                return;
              }

              setAddingCredits(true);
              try {
                const response = await apiService.addCreditsToUser(
                  userId,
                  amount,
                  creditsReason || undefined
                );

                if (response.success) {
                  alert(`Successfully added ${amount} credits to user!`);
                  setAddCreditsDialogOpen(false);
                  setCreditsAmount('');
                  setCreditsReason('');
                  
                  // Refresh transaction history and user data
                  await loadTransactions();
                } else {
                  alert('Failed to add credits: ' + (response.error || 'Unknown error'));
                }
              } catch (error: any) {
                alert('Error adding credits: ' + (error.message || 'Unknown error'));
              } finally {
                setAddingCredits(false);
              }
            }}
            variant="contained"
            color="primary"
            disabled={addingCredits || !creditsAmount}
            startIcon={addingCredits ? <CircularProgress size={20} /> : <Add />}
          >
            {addingCredits ? 'Adding...' : 'Add Credits'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserTransactions;

